import { toAbsoluteUrl } from "../../_metronic/helpers";

const defaultProfileImage = toAbsoluteUrl('/media/avatars/default.jpg')
const defaultProfileImageHeader = toAbsoluteUrl('/media/svg/nobilis/user_nb.svg')
const defaultExperienceImage = toAbsoluteUrl('/media/svg/nobilis/experience/experience-preview.jpg')
const defaultVerticalImage = toAbsoluteUrl("/media/svg/nobilis/experience/experience-vertical.jpg")
const defaultHorizontalImage = toAbsoluteUrl("/media/svg/nobilis/experience/experience-horizontal.png")
const defaultLogoBlackSVG = toAbsoluteUrl("/media/svg/nobilis/logo/nobilis-logo-black.svg")
const defaultLogoIconBlackSVG = toAbsoluteUrl("/media/svg/nobilis/logo/nobilis-icon-black.svg")
const defaultLogoNameBlackSVG = toAbsoluteUrl("/media/svg/nobilis/logo/name-only-black.svg")
const fleurSVGBlack = toAbsoluteUrl('/media/svg/nobilis/fleur.svg')
const fleurSVGGray = toAbsoluteUrl('/media/svg/nobilis/icon_landing.svg')
const exampleImages = ['/media/people3.png', '/media/people2.png', '/media/people1.png'];

export {
    defaultProfileImage,
    defaultExperienceImage,
    defaultVerticalImage,
    defaultHorizontalImage,
    defaultProfileImageHeader,
    defaultLogoBlackSVG,
    defaultLogoIconBlackSVG,
    defaultLogoNameBlackSVG,
    fleurSVGBlack,
    fleurSVGGray,
    exampleImages,
}

interface IChanges {
  feat:Array<string>
  fixes:Array<string>
}

export interface VersionInfo {
  version: string;
  updatedAt: string;
  environment: 'dev' | 'staging' | 'production';
  envoriment: 'dev' | 'staging' | 'production'; // Support alternative spelling from prompt
  changelog: IChanges
}

export const getVersionInfo = (): VersionInfo => {
  const apiUrl = process.env.REACT_APP_API_URL || '';
  
  let environment: 'dev' | 'staging' | 'production' = 'production';

  if (apiUrl.includes('dev.joinnobilis') || apiUrl.includes('127.0.0.1:8000')) {
    environment = 'dev';
  } else if (apiUrl.includes('staging.joinnobilis')) {
    environment = 'staging';
  }

  return {
    version: '0.2.10',
    updatedAt: 'Aug 28, 2026',
    environment,
    envoriment: environment,
    changelog: {
      feat: [
        "What's New in 0.2.10:",
        "- feat: Complete Experience Booking & Stripe Payment Hold workflow (manual pre-authorization and capture/cancel on host decision)",
        "- feat: Complete Private Experience Invitation workflow (in-app notification + email format 010)",
        "- feat: Instant booking auto-confirmation and immediate Stripe payment capture for invited members (bypassing host approval)",
        "- feat: Unified Requests section displaying Host booking requests with Accept/Reject actions and Guest invitations with direct detail link",
        "- feat: Automatic expiration filtering in Requests for past experience dates",
        "- feat: Multi-guest and family relatives selector with dynamic pricing (Guest vs Group rate calculation)",
        "- feat: Standardized email notification templates (Formatos 003, 004, 006, 008, 009, 010, 011, 012)",
        "- feat: Simplified and cleaned experience cards to display unified 'Guests' label across user and partner views",
        "- UAT-188: Simplified and aligned guest capacity and date display layout with Figma design. (Experiences)",
        "- UAT-201: Added support for pricing per group vs per guest with maximum group size limits. (Experiences)",
        "- UAT-210: Added placeholder text for the 'Upon Request' explanation field during experience creation. (Experiences)",
        "- UAT-212: Added total maximum/minimum capacity fields and automated pre-launch minimum threshold email. (Experiences)",
        "- UAT-213: Updated experience screen with multi-date tooltips, optional duration field, and spot badges. (Experiences)",
        "- UAT-218: Added in-app notifications, email alerts, and rejection history log when an experience is declined. (Experiences)",
        "- UAT-219: Added optional enhancements configuration per person vs per group with detailed checkout breakdown. (Experiences)",
        "- UAT-276: Restored RSVP button copy and action for 'Upon Request' experiences. (Experiences)",
        "- UAT-278: Updated Member header in Experience Details by removing the admin back button bar. (Experiences)",
        "- UAT-280: Configured private experience invitation flow requiring invitees to complete checkout to attend. (Experiences)",
        "- UAT-304: Standardized and updated email notification content and templates across the platform. (Emails)",
        "- UAT-312: Corrected copywriting on Step 3 for Confidentiality & Media Use page. (Experiences)",
        "- UAT-318: Displayed only city in public experience location while sending full address via confirmation email. (Experiences)",
        "- UAT-319: Formatted currency display to remove cents, add thousand separators, and specify USD. (Experiences)",
        "- UAT-345: Renamed tab to 'Requests' and corrected empty state text to 'No requests pending'. (Experiences)",
        "- UAT-347: Added dedicated Rejected/Declined tab for experiences with reason history and timestamp tracking. (Experiences)",
        "- UAT-348: Updated admin decline reasons catalog to match latest Nobilis criteria. (Experiences)",
        "- UAT-379: Allowed invited members to specify attendee count when purchasing a private experience. (Experiences)",
        "- UAT-382: Integrated ticketing checkout with profile relatives (name, surname, relationship) and dynamic modal. (Experiences)",
        "- UAT-384: Fixed spelling for Minimum/Maximum capacity and replaced initial 0 values with clean placeholders. (Experiences)",
        '0.2.9: Aug 20, 2026',
        "- feat: add experience suggestion workflow from creation to activation",
        "- feat: add catalog menu section and align layout with Nobilis design system",
        "- Responsive pages/components:",
        "  - waitinglist",
        "  - dashboard admin",
        "  - aside menu layout",
        "  - introductions tab bar",
        "  - edit profile button",
        "  - notifications menu",
        "  - financial table",
        "  - my introduction",
        "  - user payment method",
        "  - book an expert",
        "  - experience cards",
        "  - user my-experience",
        "  - my-team",
        '0.2.8: Aug 07, 2026',
        "- UAT-217: Added the option to remove uploaded images",
        "- UAT-305: Added drag-and-drop sorting for the image gallery",
        "- UAT-315: Disabled the private experience option",
        '0.2.7: Aug 05, 2026',
        "- UAT-308: add new email signature image for all emails",
        "- UAT-214: add new email signature for all emails",
        "0.2.6: Aug 03, 2026",
        "- Added Team user workflow, including permission assignment and the Team user menu.",
        '0.2.5: Jul 30, 2026',
        "- Added full support for Experience chat.",
        "- Added input locking while messages are being sent.",
        "- Added message editing and deletion functionality.",
        "- Added an Experience information panel in the right sidebar.",
        "- Added support for marking chat messages as read.",
        "- Fixed chat ordering based on the most recent message.",
        "- Improved chat styling and formatting.",
        "- Added AI agent improvements for gamification, including enhancements to workflows and automation.",
        '0.2.4: Jul 27, 2026',
        "- Add AI agent improvements for Expertise, Admin Suggestions, sensitive data masking, Loyalty Program, and Admin Catalogs.",
        "- Added the Messages section with support for sending and receiving messages, including attachments such as images, documents, and other file types. Experience messaging is currently in development.",
        "- Responsive pages/components:",
        " - Community: details, list, create and edit",
        " - Mastermind circles: details, list, create and edit",
        " - Admin Community",
        '0.2.3: Jul 22, 2026',
        "- Added a new section in Introductions to separate accepted introductions (My Introductions) from pending introductions (Upcoming Introductions).",
        "- Lazy load on select component",
        "- Responsive pages/components:",
        "  - Aside component",
        "  - admin biography",
        "  - admin experience",
        "  - admin MyTeam",
        "  - user Refer",
        "  - admin My Mastermind Circles",
        "  - My Membership",
        "  - Payments",
        "  - Financial overview",
        "  - Experiences edit and details",
        "  - Profile member",
        '0.2.1: Jul 10, 2026',
        'Module Admin Members',
        '- Add Introductions section',
        '- Include introduction payments in Credits & Payments history',
        '0.2.0: Jul 02, 2026',
        'Module Member Introductions',
        '- Add introduction request',
        '- Add introduction details view',
        '- Add actions to accept or reject introductions',
        'Module Admin Introductions',
        '- Add introductions list view',
        '- Add filters to search introductions',
        '- Add introduction details view',
        '- Add actions to accept or reject introductions',
        'Module Add Experiences',
        '- Updated the experience form by replacing the 10,000-character fields with a rich text editor',
        'Search',
        '- Added a section for members near the searched city',
        '- Added a section for experiences near the searched city',
        '- Added fuzzy search to improve results for country names with typing errors',
      ],
      fixes: [
        "Fixed in version 0.2.9:",
        "- UAT-395: correct multiple language display",
        "- UAT-225: correct dashboard manage button links to navigate to their respective screens",
        "- fix: add scrolling to Partner modal",
        "- fix: correct Suggestions table display",
        "- fix: square status selects on catalog admin screens",
        "- fix: restyle Create button to tap-add-experience-2-btn-secondary on catalog admin",
        "- fix: unify dashboard calendar view typography colour",
        "- fix: square dashboard task-filter selects",
        '0.2.8: Aug 07, 2026',
        "- UAT-283: Fixed styling issues in the guest modal.",
        "- UAT-211: hide empty Experience sections",
        "- UAT-313: Updated the button text for draft experiences.",
        "- UAT-314: resolve suggestion form save issue and add validation error messages",
        "- UAT-318: correct location display",
        '0.2.7: Aug 05, 2026',
        "- UAT-299: correct Team Member email format",
        '0.2.6: Aug 03, 2026',
        "- correct improve chat avatars",
        "- correct forum member count",
        "- correct sender label 'You' in chat and messages",
        '0.2.5: Jul 30, 2026',
        "- UAT-220: Fixed an issue causing errors in Experience Details.",
        '0.2.3: Jul 22, 2026',
        "- UAT-188: correct experience capacity across sections and improve multi-date handling.",
        "- UAT-191: correct experience photo carousel behavior.",
        "- UAT-192: correct reservation form and capacity limit validations.",
        "- UAT-193: corrected the experience text color to white.",
        "- UAT-194: Read More/Read Less behavior",
        "- UAT-195: Remove - and _ on Expertise title",
        "- UAT-197: Removed the visual item limit in option selectors and added lazy loading for a better user experience.",
        "- UAT-202: correct text relatives",
        "- UAT-203: correct Experience Host selector",
        "- UAT-204: correct Spanish text for the 'Optional' label in the experience form",
        "- UAT-205: correct text in the user profile information section",
        "- Fixed the information in the Introduction Details modal.",
        "- correct country selection for reservation attendees",
        "- Fixed experience booking for multiple dates.",
        "0.2.2: Jul 13, 2026",
        '- UAT-186: correct cut-off background decoration',
        '- UAT-190: remove Pause option when adding an experience',
        '- UAT-198: adjust account activation email',
        '- UAT-199: allow profile creation with only email and name',
        '- UAT-200: capitalize Expertise section text and hide price for Under Request',
        '- fix: persist current role checkbox and display text correctly',
        '0.2.1: Jul 10, 2026',
        '- UAT-166: No reset cursor on reactQuill',
        '- UAT-167: Using Language hook',
        '- UAT-168: fix text overflow and "a" tag, Fix hover and cursor events, Add width equitative',
        '- UAT-169: Expandable Description',
        '- UAT-171: Delete first element',
        '- UAT-172: Replace Carreer with Degree',
        '- UAT-173: Delete "-" on profile name/alias',
        '- UAT-174: resolve profile editing issue across member profile sections',
        '- UAT-176: disable email by default',
        '- UAT-177: Update email sent for activating account',
        '- UAT-178: Default set "2 years" value on duration',
        '- UAT-181: Introduction headline is not required',
        '- UAT-183: replace plain text with react quill / rich text',
        '- UAT-185: show only experiences from the same member in Invites You details',
        '- Add pagination to Community and Mastermind Circle',
        '- Filter host experiences in experience details and update pending field labels',
        '0.2.0: Jul 02, 2026',
        '- Mailing: Removed the "Team Member" section from the account activation email',
      ]
    }
  };
};